import { forwardRef, useCallback, useRef } from 'react';
import { splitSkuLabel, type ItemSearchField } from '@/hooks/inventory';
import { sameSkuCode, sameSkuName } from './itemMatch';
import { highlight } from './highlight';
import { SearchSelect, type PickSource, type SearchOption, type SearchSelectHandle } from './SearchSelect';

/** Item search for a picker box: by code, by name, or — with no field — both. */
export type ItemSearch = (query: string, by?: ItemSearchField) => Promise<SearchOption[]>;

const isCode = (option: SearchOption, typed: string) => sameSkuCode(splitSkuLabel(option.label).code, typed);
const isName = (option: SearchOption, typed: string) => sameSkuName(splitSkuLabel(option.label).name, typed);

/** The column header over a line's pair of boxes, laid out on the same grid. */
export function ItemPickerHead() {
  return (
    <span className="itempicker">
      <span>SKU *</span>
      <span>Product name *</span>
    </span>
  );
}

/**
 * Picks a line's item with two linked boxes, the way `StorePicker` picks a
 * store: its SKU and its product name.
 *
 * A storekeeper reading codes off an invoice types `F-52-01`; one who knows
 * the item by name types "grower". Whichever box is used, the other fills
 * itself — both show the one chosen item, so they can never disagree.
 *
 * A SKU or a name typed in full is taken on Tab without opening the list, as
 * is a code typed (or scanned) faster than its search returns. A name two
 * old items still share is never chosen for the user: the list shows each
 * one's SKU to choose by.
 */
export const ItemPicker = forwardRef<
  SearchSelectHandle,
  {
    /** The line's position, 0-based — the boxes are "Item 1 SKU" and so on. */
    index: number;
    value: string;
    /** The chosen item as `skuLabel` wrote it, read back into code and name. */
    selectedLabel: string;
    search: ItemSearch;
    disabled?: boolean | undefined;
    /** Shown in the name box when nothing is chosen. */
    placeholder?: string | undefined;
    autoFocus?: boolean | undefined;
    onChange: (option: SearchOption | null) => void;
    /** After an item is chosen, so the line can move on to its quantity. */
    onPick?: (() => void) | undefined;
  }
>(function ItemPicker(
  { index, value, selectedLabel, search, disabled, placeholder, autoFocus, onChange, onPick },
  ref,
) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const n = index + 1;
  const chosen = value ? splitSkuLabel(selectedLabel) : { code: '', name: '' };

  const byCode = useCallback((query: string) => search(query, 'code'), [search]);
  const byName = useCallback((query: string) => search(query, 'name'), [search]);

  function pick(option: SearchOption | null) {
    // The item already on the line, found again from the other box: nothing
    // changes, so its lot and expiry stay as they were.
    if (option && option.value === value) return;
    onChange(option);
  }

  /**
   * Enter or a click moves straight on to the quantity. Tab already moves
   * focus itself, so it is only followed while that left focus inside the
   * pair — SKU into Product name, which needs no checking once the SKU has
   * filled it. Leaving any other way (Shift+Tab, a click elsewhere) never
   * pulls focus back.
   */
  function afterPick(_option: SearchOption, source: PickSource) {
    if (source === 'blur') return;
    if (source !== 'tab') {
      onPick?.();
      return;
    }
    setTimeout(() => {
      if (wrapRef.current?.contains(document.activeElement)) onPick?.();
    }, 0);
  }

  /** What the box searches leads the row; the other half and the unit (or
   *  stock held) follow on a quiet line under it. */
  function row(option: SearchOption, query: string, by: ItemSearchField) {
    const { code, name } = splitSkuLabel(option.label);
    const main = by === 'code' ? code : name;
    const rest = [by === 'code' ? name : code, option.meta].filter(Boolean).join(' · ');
    return (
      <span className="pickopt">
        <span className={`pickopt__main${by === 'code' ? ' pickopt__main--code' : ''}`} title={main}>
          {highlight(main, query)}
        </span>
        <span className="pickopt__sub" title={rest}>
          {rest}
        </span>
      </span>
    );
  }

  return (
    <div ref={wrapRef} className="itempicker" role="group" aria-label={`Item ${n}`}>
      <SearchSelect
        ref={ref}
        label={`Item ${n} SKU`}
        visibleLabel="SKU"
        labelClassName="itemlines__label"
        required
        disabled={disabled}
        value={value}
        selectedLabel={chosen.code}
        search={byCode}
        placeholder="SKU"
        emptyText="No item with that SKU"
        autoFocus={autoFocus}
        commitExact
        isExact={isCode}
        onChange={pick}
        onPick={afterPick}
        renderOption={(o, q) => row(o, q, 'code')}
      />
      <SearchSelect
        label={`Item ${n} product name`}
        visibleLabel="Product name"
        labelClassName="itemlines__label"
        required
        disabled={disabled}
        value={value}
        selectedLabel={chosen.name}
        search={byName}
        placeholder={placeholder ?? 'Search by product name…'}
        emptyText="No item with that name"
        commitExact
        isExact={isName}
        onChange={pick}
        onPick={afterPick}
        renderOption={(o, q) => row(o, q, 'name')}
      />
    </div>
  );
});
